/**
 * Phase B+1.1 — IdeaWorkspace 左ペイン上部の MapLibre mini-map (旧 IdeaForm L799-869 + L523-598)。
 *
 * 旧 v1 の `mini-map` div + `usePmtilesMap` フックを v2 に揃え直したもの。
 *   - front mode: `selectedPosts` を 1..N 番号付き marker で配置 (size 32 vs 24 で active 切替)
 *   - back mode:  `selectedInsights` を INSIGHT_COLOR_PALETTE 8 色で色分け
 *     (各 insight の selectedPostIds を該当 post に紐づけて plot、active insight は強調)
 *
 * 旧と違う点 (意図):
 *   - `usePmtilesMap` フックは v1 にしか存在しない → v2 では `features/map` の
 *     `useDistrictPmtiles` + `addPmtilesSource` を直接呼ぶ最小版で代替。
 *     OSM ラスター fallback を default にし、pmtiles がキャッシュにあれば自動で差し替える。
 *   - preserveDrawingBuffer:true を Map 生成時に指定 (旧 L519、地図キャプチャ用)。
 *   - 番号付き marker DOM は `insightMapHelpers.createNumberedMarkerEl` 経由。
 *
 * 利用側 (`IdeaWorkspace.tsx`) は `getMapHandle` を ref 経由で受取り、
 * Step 6 の「🗺 地図をキャプチャ」button で `map.getCanvas().toDataURL()` を呼ぶ想定。
 */

import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { type Map as MaplibreMap } from 'maplibre-gl';
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import type { PostMinimal } from '../posts';
// docs/107 オフライン残: glyphs + 地名 symbol 付き pmtiles スタイルは barrel 経由 (deep import 回避)
import {
  addPmtilesSource,
  buildPmtilesStyle,
  lookupValidCache,
  useDistrictPmtiles,
  type OfflineMapEntry,
} from '../map';
import { createNumberedMarkerEl, fitBoundsOnce, getInsightColor } from './insightMapHelpers';
import type { InsightMinimal } from './useEcwInsights';

/* ============================================================================
 * Types
 * ========================================================================== */

export type IdeaMiniMapMode = 'front' | 'back';

export interface IdeaWorkspaceMiniMapProps {
  districtId: string;
  mode: IdeaMiniMapMode;
  /** front mode の plot ソース。 */
  selectedPosts: PostMinimal[];
  /** front mode で「選択中 (中央詳細表示中) の post」をハイライト。 */
  selectedPost?: PostMinimal | null;
  /** back mode の plot ソース。各 insight.selectedPostIds を selectedPosts から解決して plot。 */
  selectedInsights?: InsightMinimal[];
  /** back mode で「選択中の insight」を強調 (全 marker を size 32 にする)。 */
  selectedInsightId?: string | null;
  /** Marker クリック時 callback (front: post、back: post + insight)。 */
  onSelectPost?: (post: PostMinimal) => void;
  onSelectInsight?: (insight: InsightMinimal) => void;
  /** CSS class (高さ等の親 layout 上書き用)。 */
  className?: string;
}

/** 親 (IdeaWorkspace) から `map.getCanvas().toDataURL()` 等を呼ぶための ref handle。 */
export interface IdeaWorkspaceMiniMapHandle {
  /** 地図キャプチャ用。preserveDrawingBuffer:true で生成済 → triggerRepaint + idle 待ち後に dataURL。 */
  capture: () => Promise<string | null>;
  /** map.resize() を外部から強制 (pane visibility 切替時)。 */
  resize: () => void;
}

/* ============================================================================
 * Constants
 * ========================================================================== */

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const DEFAULT_CENTER: [number, number] = [139.7, 35.6]; // [lng, lat]
const DEFAULT_ZOOM = 13;

/* ============================================================================
 * Component
 * ========================================================================== */

export const IdeaWorkspaceMiniMap = forwardRef<
  IdeaWorkspaceMiniMapHandle,
  IdeaWorkspaceMiniMapProps
>(function IdeaWorkspaceMiniMap(props, ref) {
  const {
    districtId,
    mode,
    selectedPosts,
    selectedPost = null,
    selectedInsights = [],
    selectedInsightId = null,
    onSelectPost,
    onSelectInsight,
    className,
  } = props;

  const container = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const blobUrlRef = useRef<string | null>(null);
  const [layersReady, setLayersReady] = useState(false);
  const pmtilesState = useDistrictPmtiles(districtId || null);

  /* ---- Imperative handle (caller: IdeaWorkspace Step6 capture) ----
   * Phase B+2-3: 地図 canvas (WebGL タイル) に番号付きピンを 2D canvas で重ね焼きする。
   * MapLibre の Marker は DOM 要素なので map.getCanvas() には含まれない (旧 v1 は html2canvas
   * の onclone callback で WebGL→<img> 差替えて合成していた)。v2 は単純な 2D canvas 合成で同等表現。
   */
  useImperativeHandle(
    ref,
    (): IdeaWorkspaceMiniMapHandle => ({
      capture: async () => {
        const map = mapRef.current;
        if (!map) return null;
        try {
          map.triggerRepaint();
          await new Promise<void>((resolve) => {
            try {
              map.once('idle', () => resolve());
              // jsdom mock では idle が発火しないので 200ms で fallback resolve
              setTimeout(() => resolve(), 200);
            } catch {
              resolve();
            }
          });
          const mapCanvas = map.getCanvas();
          if (!mapCanvas || typeof mapCanvas.toDataURL !== 'function') return null;

          // Composite canvas (map + markers)
          let composite: HTMLCanvasElement;
          let ctx: CanvasRenderingContext2D | null;
          try {
            composite = document.createElement('canvas');
            composite.width = mapCanvas.width;
            composite.height = mapCanvas.height;
            ctx = composite.getContext('2d');
          } catch {
            // 環境によっては createElement 失敗 → map のみ返す
            return mapCanvas.toDataURL('image/png');
          }
          if (!ctx) {
            return mapCanvas.toDataURL('image/png');
          }
          try {
            ctx.drawImage(mapCanvas, 0, 0);
          } catch {
            // CORS tainted 等で描画不可 → map のみ
            return mapCanvas.toDataURL('image/png');
          }

          // dpr: getCanvas() の物理 px / CSS px
          const cssW = mapCanvas.clientWidth || mapCanvas.width;
          const dpr = cssW > 0 ? mapCanvas.width / cssW : 1;

          // ピン 1 個を描画 (旧 idea-map-marker の DOM スタイルを 2D canvas で近似)
          const drawPin = (
            lng: number,
            lat: number,
            num: number,
            color: string,
            active: boolean,
          ): void => {
            try {
              const point = map.project([lng, lat]);
              const cx = point.x * dpr;
              const cy = point.y * dpr;
              const r = (active ? 16 : 12) * dpr;
              // shadow
              ctx.save();
              ctx.shadowColor = 'rgba(0,0,0,0.3)';
              ctx.shadowBlur = 4 * dpr;
              ctx.shadowOffsetY = 1 * dpr;
              ctx.beginPath();
              ctx.arc(cx, cy, r, 0, Math.PI * 2);
              ctx.fillStyle = color;
              ctx.fill();
              ctx.restore();
              // border
              ctx.beginPath();
              ctx.arc(cx, cy, r, 0, Math.PI * 2);
              ctx.lineWidth = 2 * dpr;
              ctx.strokeStyle = '#ffffff';
              ctx.stroke();
              // number
              ctx.fillStyle = '#ffffff';
              ctx.font = `bold ${(active ? 14 : 11) * dpr}px sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(String(num + 1), cx, cy);
            } catch {
              // map.project / arc 失敗は silent skip
            }
          };

          if (mode === 'front') {
            selectedPosts.forEach((p, idx) => {
              if (!p.location) return;
              const lat = p.location.latitude;
              const lng = p.location.longitude;
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
              if (lat === 0 && lng === 0) return;
              const active = selectedPost?.id === p.id;
              drawPin(lng, lat, idx, '#00B4D8', active);
            });
          } else {
            const postById = new Map<string, { post: PostMinimal; index: number }>();
            selectedPosts.forEach((p, i) => postById.set(p.id, { post: p, index: i }));
            selectedInsights.forEach((insight, insightIdx) => {
              const color = getInsightColor(insightIdx);
              const insightActive = selectedInsightId === insight.id;
              insight.selectedPostIds.forEach((postId) => {
                const found = postById.get(postId);
                if (!found) return;
                const { post: p, index: postIndex } = found;
                if (!p.location) return;
                const lat = p.location.latitude;
                const lng = p.location.longitude;
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                if (lat === 0 && lng === 0) return;
                const postActive = selectedPost?.id === p.id;
                const active = insightActive || postActive;
                drawPin(lng, lat, postIndex, color, active);
              });
            });
          }

          return composite.toDataURL('image/png');
        } catch (err) {
          console.warn('[IdeaWorkspaceMiniMap] capture failed', err);
          return null;
        }
      },
      resize: () => {
        try {
          mapRef.current?.resize();
        } catch {
          // jsdom mock 等で silent
        }
      },
    }),
    [mode, selectedPosts, selectedPost, selectedInsights, selectedInsightId],
  );

  /* ---- pmtiles blob resolution (use cached blob if exists、なければ OSM) ---- */
  const [tileSource, setTileSource] = useState<'osm' | 'pmtiles' | 'pending'>('pending');
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (pmtilesState.status === 'loading') return;
      if (pmtilesState.status === 'ready' && pmtilesState.meta && districtId) {
        try {
          const cached: OfflineMapEntry | null = await lookupValidCache(
            districtId,
            pmtilesState.meta,
          );
          if (cancelled) return;
          if (cached) {
            const url = URL.createObjectURL(cached.blob);
            blobUrlRef.current = url;
            setTileSource('pmtiles');
            return;
          }
        } catch {
          // ignore — fallback to OSM
        }
      }
      if (!cancelled) setTileSource('osm');
    }
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [pmtilesState, districtId]);

  /* ---- Map 生成 (tileSource 確定後 1 回) ---- */
  useEffect(() => {
    if (!container.current) return;
    if (tileSource === 'pending') return;

    let style: maplibregl.StyleSpecification;
    if (tileSource === 'pmtiles' && blobUrlRef.current) {
      // docs/107 オフライン残: glyphs (Protomaps CDN) + 地名 symbol layer 付きの schema 対応スタイル。
      const pmtilesUrl = addPmtilesSource(blobUrlRef.current);
      const pmtilesSchema =
        pmtilesState.status === 'ready' ? (pmtilesState.meta?.schema ?? 'protomaps') : 'protomaps';
      style = buildPmtilesStyle(pmtilesUrl, pmtilesSchema);
    } else {
      style = {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: [OSM_TILE_URL],
            tileSize: 256,
            attribution: OSM_ATTRIBUTION,
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      };
    }

    let map: MaplibreMap;
    try {
      map = new maplibregl.Map({
        container: container.current,
        style,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        // 旧 IdeaForm L519 踏襲: 地図キャプチャ (Step 6) のため WebGL buffer を保持。
        preserveDrawingBuffer: true,
      });
    } catch (err) {
      console.warn('[IdeaWorkspaceMiniMap] map init failed', err);
      return;
    }
    mapRef.current = map;

    const handleLoad = () => setLayersReady(true);
    if (typeof map.loaded === 'function' && map.loaded()) {
      handleLoad();
    } else {
      try {
        map.once('load', handleLoad);
      } catch {
        // jsdom mock: silent — マーカー描画 effect を待たずに layersReady を true に
        handleLoad();
      }
    }

    return () => {
      try {
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];
        map.remove();
      } catch {
        // ignore
      }
      mapRef.current = null;
      setLayersReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileSource]);

  /* ---- blob URL revoke on unmount ---- */
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        try {
          URL.revokeObjectURL(blobUrlRef.current);
        } catch {
          // ignore
        }
        blobUrlRef.current = null;
      }
    };
  }, []);

  /* ---- Marker 配置 + fitBounds (旧 IdeaForm L523-598) ---- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !layersReady) return;

    // 既存 marker を全消去
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    let bounds: maplibregl.LngLatBounds;
    try {
      bounds = new maplibregl.LngLatBounds();
    } catch {
      // jsdom mock: LngLatBounds 未実装 → 何もしない
      return;
    }
    let hasPoint = false;

    if (mode === 'front') {
      // 前半: selectedPosts を 1..N でプロット
      selectedPosts.forEach((p, idx) => {
        if (!p.location) return;
        const lat = p.location.latitude;
        const lng = p.location.longitude;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (lat === 0 && lng === 0) return;
        try {
          bounds.extend([lng, lat]);
        } catch {
          // ignore
        }
        hasPoint = true;
        const active = selectedPost?.id === p.id;
        const el = createNumberedMarkerEl(idx, {
          active,
          color: '#00B4D8',
          onClick: () => onSelectPost?.(p),
        });
        try {
          const marker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
          markersRef.current.push(marker);
        } catch {
          // jsdom mock
        }
      });
    } else {
      // 後半: insight ごとに 8 色で色分けし、紐づく posts を plot
      const postById = new Map<string, { post: PostMinimal; index: number }>();
      selectedPosts.forEach((p, i) => postById.set(p.id, { post: p, index: i }));

      selectedInsights.forEach((insight, insightIdx) => {
        const color = getInsightColor(insightIdx);
        const insightActive = selectedInsightId === insight.id;
        insight.selectedPostIds.forEach((postId) => {
          const found = postById.get(postId);
          if (!found) return;
          const { post: p, index: postIndex } = found;
          if (!p.location) return;
          const lat = p.location.latitude;
          const lng = p.location.longitude;
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          if (lat === 0 && lng === 0) return;
          try {
            bounds.extend([lng, lat]);
          } catch {
            // ignore
          }
          hasPoint = true;
          const postActive = selectedPost?.id === p.id;
          const active = insightActive || postActive;
          const el = createNumberedMarkerEl(postIndex, {
            active,
            color,
            size: active ? 32 : 20,
            onClick: () => {
              onSelectInsight?.(insight);
              onSelectPost?.(p);
            },
          });
          try {
            const marker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
            markersRef.current.push(marker);
          } catch {
            // jsdom mock
          }
        });
      });
    }

    if (hasPoint) {
      fitBoundsOnce(map, bounds, { padding: 20, maxZoom: 18, duration: 0 });
    }
    // resize 100ms 後 (旧 L591 踏襲、コンテナサイズ確定待ち)
    const t = window.setTimeout(() => {
      try {
        map.resize();
      } catch {
        // ignore
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [
    layersReady,
    mode,
    selectedPosts,
    selectedPost?.id,
    selectedInsights,
    selectedInsightId,
    onSelectPost,
    onSelectInsight,
  ]);

  return (
    <div
      ref={container}
      className={
        className ?? 'w-full h-40 rounded border border-slate-200 overflow-hidden bg-slate-100'
      }
      data-testid="idea-workspace-mini-map"
      data-mode={mode}
    />
  );
});
